import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { SubjectsClassified } from '../../../ampli';
import OnboardingStepWichtigkeitImage from '../../../assets/onboarding/onboarding-step-wichtigkeit.svg';
import { routes } from '../../../constants';
import { selectIsStripeSchoolAdmin, selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import useAction from '../../../utils/useAction';
import useRouter from '../../../utils/useRouter';
import type { Option } from '../OnboardingOptionChipList';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';
import useOnboardingSave from '../useOnboardingSave';
import DraggableSubjectList from './DraggableSubjectList';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	initialSelectedSubjects: Option[];
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
}

const OnboardingStepWichtigkeit: React.FC<OwnProps> = ({
	setOnboardingStep,
	initialSelectedSubjects,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
}) => {
	const { t } = useTranslation();
	const { router } = useRouter();
	const user = useSelector(selectUser);
	const isStripeSchoolAdmin = useSelector(selectIsStripeSchoolAdmin);
	const updateMe = useAction(updateMeAction);
	const save = useOnboardingSave();
	const [subjectsOrder, setSubjectsOrder] = useState<Option[]>(initialSelectedSubjects);

	const contentHeights = {
		default: 'calc(100vh - 527px)',
		smallScreen: 'calc(100vh - 353px)',
	};

	if (!initialSelectedSubjects || initialSelectedSubjects.length === 0) {
		setOnboardingStep('schulfaecher');
		return null;
	}

	const getCompletionDestination = () => {
		if (user?.isAiOnlyUser) {
			return `/${routes.aiTools.root}`;
		}
		if (isStripeSchoolAdmin) {
			return `/${routes.stripeSchoolAdminTeachers.root}`;
		}
		return `/${routes.dashboard.root}?isOnboardingComplete=true`;
	};

	const handleSubmit = () =>
		save(async () => {
			if (!user) return;

			const subjects = {
				majors: subjectsOrder.map((subject, i) => ({
					subject: subject.value,
					priority: i,
				})),
				minors: [],
				prioritized: false,
			};

			if (onNextOverride) {
				await updateMe({ subjects });
			} else {
				await updateMe({
					subjects,
					data: {
						onboarding: {
							finished: true,
						},
					},
				});
			}

			// Tracked only once the save went through, like the other steps.
			services.track.ampliEventTrack({
				event: new SubjectsClassified(),
				userProperties: {
					id: user.id,
					subjects,
				},
			});

			if (onNextOverride) {
				onNextOverride();
			} else {
				router.push(getCompletionDestination());
			}
		});

	return (
		<>
			<OnboardingStepContainer
				title={t('Organize your subjects by importance')}
				subtitle={t(
					'Prioritize your school subjects so we can better personalize the content for you.'
				)}
				stepNum={stepNum ?? 6}
				totalSteps={totalSteps}
				onBack={
					onBackOverride === undefined
						? () => setOnboardingStep('schulfaecher')
						: (onBackOverride ?? undefined)
				}
				onNext={handleSubmit}
				contentHeights={contentHeights}
				compact={compact}
			>
				<DraggableSubjectList items={subjectsOrder} onChange={setSubjectsOrder} />
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepWichtigkeitImage />} />}
		</>
	);
};

export default OnboardingStepWichtigkeit;
